import { get, find, toLower, toUpper, pick, sortBy } from "lodash"
import { createSelector } from "reselect"
import { getModels } from "resources/models"

export const getPortfolio = state => get(state, "global.data.editPortfolio.data")
export const getModelCurrentDate = state => get(state, "global.data.editPortfolioDates.data.model.availability.currentDate")

const getBenchmarks = state => get(state, "global.data.benchmarks.data.model.benchmarks")
const getBetaBenchmarkId = (_, props) => get(props, "portfolio.betaBenchmarkId")
const getBetaIndexRaw = state => get(state, "global.data.betaIndex.data.index")
const getBetaResourceRaw = state => get(state, "global.data.betaResource.data.resource")
const getBetaSecurityMeta = state => get(state, "global.data.betaSecurity.data.model.security.descriptors")

export const getSortedModels = createSelector(
  [getModels],
  (models) => sortBy(models, item => item.name),
)

const getBetaBenchmarkMeta = createSelector(
  [getBenchmarks, getBetaBenchmarkId],
  (benchmarks, benchmarkId) => {
    if (!benchmarkId) {
      return null
    }

    const benchmark = find(benchmarks, { id: benchmarkId })
    if (benchmark) {
      return {
        type: "benchmark",
        ...pick(benchmark, "id", "name"),
      }
    }

    // benchmark not found, return a default "unavailable" version
    return {
      type: "benchmark",
      id: benchmarkId,
      name: benchmarkId,
      isUnavailable: true,
    }
  },
)

const getBetaResourceMeta = createSelector(
  [getPortfolio, getBetaResourceRaw],
  (portfolio, resource) => {
    if (get(portfolio, "betaResourceId") && !resource) {
      // resource ID is set but resource is not found, assume user does not have permission
      return {
        name: "Unknown Resource",
        isPermissionDenied: true,
      }
    }

    if (!resource) {
      return null
    }

    if (toLower(get(resource, "type")) === "swap") {
      return {
        assetClass: "swap",
        name: get(resource, "description"),
        ticker: toUpper(get(resource, "alias")),
      }
    }

    return {
      type: toLower(get(resource, "type")),
      name: get(resource, "name"),
      description: get(resource, "description"),
    }
  },
)

const getBetaSecurity = createSelector(
  [getPortfolio, getBetaSecurityMeta],
  (portfolio, securityMeta) => {
    const betaSecurityId = get(portfolio, "betaSecurityId")

    if (!betaSecurityId) {
      return null
    }

    const securityIdAttributes = ["ticker", "mic", "internalId", "figi", "bloombergTicker"]

    if (securityMeta) {
      return {
        assetClass: "equity",
        assetSubclass: "etf",
        ...pick(securityMeta, ["name", ...securityIdAttributes]),
      }
    }

    // beta security was not found, returning a default "unmodeled" version
    const { ticker, mic, figi, bloombergTicker } = betaSecurityId
    return {
      assetClass: "equity",
      assetSubclass: "etf",
      name: (ticker && mic) ? `${ticker}.${mic}` : figi || bloombergTicker || "Unmodeled Security",
      ...(pick(betaSecurityId, securityIdAttributes)),
      isUnmodeled: true,
    }
  })

const getBetaIndexMeta = createSelector(
  [getPortfolio, getBetaIndexRaw],
  (portfolio, index) => {
    if (!get(portfolio, "betaIndexId")) {
      return null
    }

    if (!index) {
      // index ID is set but index is not found, assume user does not have permission
      return {
        name: "Unknown Index",
        isPermissionDenied: true,
      }
    }

    return {
      type: "index",
      id: get(index, "id"),
      name: get(index, "name"),
    }
  })

export const getBetaInstrumentMeta = createSelector(
  [getBetaBenchmarkMeta, getBetaIndexMeta, getBetaResourceMeta, getBetaSecurity],
  (benchmark, index, resource, security) => {
    return benchmark || index || resource || security
  },
)
